import { RefreshCw } from 'lucide-react';

/**
 * ============================================================================
 * ⭐⭐ RE-READ THE PILE — ON THE HEADER, ON BOTH TRAYS
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"Ok moved a file from local to sharepoint. Local doesn't
 * > have a refresh so both files are showing up in both spots."* … *"Need that
 * > little refresh button in upper right hand corner"*.
 *
 * ---- ⛔ IT WAS IN THE BODY, WHICH IS THE ONE PLACE IT IS OFTEN NOT ---------
 *
 * ⚠️ `PaperworkSliver` drew this inside its scrolling body, so it existed only
 * while the section was EXPANDED and only while the tray was in the scanned
 * view. ⛔ Since #318 the panel starts COLLAPSED, and the attached view has no
 * body of its own — so at the two moments a scheduler most wants to say "look
 * again" there was no control to press.
 *
 * ⭐ THE HEADER IS ALWAYS DRAWN. Open, folded, empty, attached — the row is
 * there, so the control is there.
 *
 * ---- ⛔⛔ AND `ScanFolderPanel` NEVER HAD ONE AT ALL ----------------------
 *
 * The local tray polls every few seconds, and that was taken as "it does not
 * need a button". ⚠️ IT DOES: the poll is PAUSED WHILE THE SECTION IS FOLDED,
 * and a scheduler who has just dropped a scan onto the cloud icon wants the
 * grey-out NOW, not on the next tick. §4.14.3 — one of two trays given a
 * feature — is why this is a component rather than a second copy of the JSX.
 *
 * ⛔ `shrink-0`, LIKE EVERYTHING ELSE IN THAT ROW. The header is a flex row and
 * an icon without it is crushed to nothing when the title is long — the exact
 * fault that lost the chevron twice on 2026-09-10.
 * ============================================================================
 */
export function TrayRefreshButton({
    onClick, busy = false, title = 'Look again',
}: {
    onClick: () => void;
    busy?: boolean;
    title?: string;
}) {
    return (
        <button
            type="button"
            /* ⛔ THE HEADER'S FOLD BUTTON IS ITS SIBLING, NOT ITS PARENT — but a
               click here still must not reach anything else that may wrap the
               row, so it is stopped explicitly rather than by luck of layout. */
            onClick={(e) => { e.stopPropagation(); if (!busy) onClick(); }}
            /* ⚠️ `aria-disabled`, NOT `disabled`. Same rule as `ScanSourceToggle`:
               a disabled button receives no events at all, and this row is inside
               a drag-and-drop surface where an inert element reads as broken. The
               click is guarded below instead. */
            aria-disabled={busy}
            title={title}
            className={'shrink-0 rounded p-1 text-muted-foreground transition-colors '
                + (busy ? 'opacity-40' : 'hover:bg-muted hover:text-foreground')}
        >
            <RefreshCw className={'h-3.5 w-3.5 ' + (busy ? 'animate-spin' : '')} />
            <span className="sr-only">Refresh</span>
        </button>
    );
}
